import { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { Image as ImageIcon, Upload, Trash2, ExternalLink, Cloud, HelpCircle, Video as VideoIcon } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";

function isVideo(contentType, filename) {
  if (contentType && contentType.startsWith("video/")) return true;
  return /\.(mp4|webm|mov|m4v|ogg)$/i.test(filename || "");
}

export default function Gallery() {
  const { isAdmin } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploader, setUploader] = useState(() => localStorage.getItem("ebel_author") || "");
  const [caption, setCaption] = useState("");
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);
  const token = localStorage.getItem("ebel_token");

  const load = () => api.get("/photos").then((r) => setPhotos(r.data)).finally(() => setLoading(false));
  useEffect(() => {
    load();
    api.get("/info").then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setBusy(true);
    const author = uploader.trim();
    if (author) localStorage.setItem("ebel_author", author);
    let ok = 0;
    let fail = 0;
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("caption", caption);
      fd.append("uploader", author);
      try {
        // eslint-disable-next-line no-await-in-loop
        await api.post("/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
        ok += 1;
      } catch {
        fail += 1;
      }
    }
    setCaption("");
    if (fileRef.current) fileRef.current.value = "";
    await load();
    if (ok > 0) toast.success(`${ok} Datei${ok > 1 ? "en" : ""} hochgeladen`);
    if (fail > 0) toast.error(`${fail} Datei${fail > 1 ? "en" : ""} fehlgeschlagen`);
    setBusy(false);
  };

  const remove = async (id) => {
    if (!window.confirm("Eintrag wirklich entfernen?")) return;
    await api.delete(`/photos/${id}`);
    await load();
    toast.success("Eintrag entfernt");
  };

  return (
    <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16" data-testid="gallery-page">
      <div className="text-center mb-10">
        <p className="label-eyebrow mb-3">Erinnerungen</p>
        <h1 className="font-serif text-5xl lg:text-6xl tracking-tight">Bilder & Galerie</h1>
        {info?.gallery_intro && (
          <p className="text-[#4A4F46] mt-4 max-w-2xl mx-auto leading-relaxed whitespace-pre-line">{info.gallery_intro}</p>
        )}
      </div>

      {info?.gallery_usage && (
        <div className="bg-[#FDF1EA] border border-[#C97B5D]/30 rounded-2xl p-5 mb-8 flex gap-3" data-testid="gallery-usage">
          <HelpCircle className="w-5 h-5 text-[#C97B5D] shrink-0 mt-0.5" />
          <div className="text-[#1A1C19] leading-relaxed whitespace-pre-line text-sm">{info.gallery_usage}</div>
        </div>
      )}

      {info?.gallery_links && info.gallery_links.filter((l) => l.url && l.label).length > 0 && (
        <div className="mb-10" data-testid="gallery-cloud-links">
          <div className="flex items-center gap-2 text-[#3F6E96] mb-4">
            <Cloud className="w-5 h-5" />
            <h2 className="font-serif text-2xl tracking-tight text-[#1A1C19]">Externe Foto-Sammlungen</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {info.gallery_links.filter((l) => l.url && l.label).map((l, i) => (
              <a key={`${i}-${l.url}`} href={l.url} target="_blank" rel="noreferrer" className="block bg-white border border-[#E5DFD3] hover:border-[#3F6E96] rounded-2xl p-5 transition-colors group">
                <div className="flex items-center justify-between gap-3 mb-1">
                  <span className="font-serif text-xl tracking-tight text-[#1A1C19]">{l.label}</span>
                  <ExternalLink className="w-4 h-4 text-[#3F6E96] shrink-0 group-hover:translate-x-0.5 transition-transform" />
                </div>
                {l.note && <p className="text-sm text-[#4A4F46] leading-relaxed mt-1 whitespace-pre-line">{l.note}</p>}
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Upload-Box */}
      <div className="bg-white border border-[#E5DFD3] rounded-2xl p-6 mb-10 shadow-[0_8px_30px_-20px_rgba(72,99,74,0.3)]" data-testid="gallery-upload-form">
        <div className="flex items-center gap-2 text-[#48634A] mb-4">
          <Upload className="w-4 h-4" />
          <span className="label-eyebrow">Eigenes Bild oder Video hochladen</span>
        </div>
        <div className="grid md:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="label-eyebrow block mb-2">Mein Name</label>
            <input type="text" value={uploader} onChange={(e) => setUploader(e.target.value)} placeholder="z. B. Anna Ebel" maxLength={80}
              className="w-full bg-[#FDFBF7] border border-[#E5DFD3] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#48634A]/30" data-testid="gallery-uploader-name" />
          </div>
          <div>
            <label className="label-eyebrow block mb-2">Beschreibung (optional)</label>
            <input type="text" value={caption} onChange={(e) => setCaption(e.target.value)} placeholder="z. B. Sommerfest 2024" maxLength={500}
              className="w-full bg-[#FDFBF7] border border-[#E5DFD3] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#48634A]/30" data-testid="gallery-caption" />
          </div>
        </div>
        <input ref={fileRef} type="file" accept="image/*,video/*" multiple onChange={onUpload} disabled={busy}
          className="block text-sm file:btn-primary file:rounded-xl file:border-0 file:px-4 file:py-2 file:mr-3 file:cursor-pointer" data-testid="gallery-file-input" />
        <p className="text-xs text-[#4A4F46] mt-2 italic">Bilder (jpg/png/gif/webp) oder Videos (mp4/webm/mov). Mehrere Dateien auf einmal möglich.</p>
      </div>

      {loading && <p className="text-center text-[#4A4F46]">Lade …</p>}

      {!loading && photos.length === 0 && (
        <div className="text-center py-20 border border-dashed border-[#E5DFD3] rounded-2xl">
          <ImageIcon className="w-10 h-10 text-[#48634A] mx-auto mb-4" />
          <p className="font-serif text-2xl">Noch keine Beiträge</p>
          <p className="text-[#4A4F46] mt-2">Sei die/der Erste!</p>
        </div>
      )}

      {!loading && photos.length > 0 && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="gallery-grid">
          {photos.map((p) => {
            const video = isVideo(p.content_type, p.original_filename);
            const src = `${API}/photos/${p.id}/download?auth=${token}`;
            return (
              <figure key={p.id} className="bg-white border border-[#E5DFD3] rounded-2xl overflow-hidden shadow-[0_8px_30px_-20px_rgba(72,99,74,0.25)] flex flex-col" data-testid={`photo-${p.id}`}>
                <div className="aspect-[4/3] bg-[#F4EFE6] overflow-hidden relative">
                  {video ? (
                    <video controls src={src} className="w-full h-full object-cover bg-black" preload="metadata" />
                  ) : (
                    <a href={src} target="_blank" rel="noreferrer" className="block w-full h-full">
                      <img src={src} alt={p.caption || p.original_filename} className="w-full h-full object-cover transition-transform duration-500 hover:scale-105" />
                    </a>
                  )}
                  {video && (
                    <span className="absolute top-2 left-2 bg-black/60 text-white text-xs px-2 py-1 rounded-full inline-flex items-center gap-1">
                      <VideoIcon className="w-3 h-3" /> Video
                    </span>
                  )}
                </div>
                <figcaption className="p-4 flex-1 flex flex-col">
                  {p.caption ? (
                    <p className="font-serif text-lg text-[#1A1C19] leading-snug">{p.caption}</p>
                  ) : (
                    <p className="font-serif text-lg text-[#4A4F46] italic">Ohne Beschreibung</p>
                  )}
                  <div className="mt-2 flex items-center justify-between gap-2 text-xs text-[#4A4F46]">
                    <span>
                      {p.uploader && <>von <strong className="text-[#1A1C19] not-italic">{p.uploader}</strong> · </>}
                      {formatDate(p.created_at)}
                    </span>
                    {isAdmin && (
                      <button
                        onClick={() => remove(p.id)}
                        className="bg-[#B74134] text-white hover:bg-[#8a3025] rounded-full px-3 py-1 inline-flex items-center gap-1 font-medium"
                        data-testid={`photo-delete-${p.id}`}
                      >
                        <Trash2 className="w-3.5 h-3.5" /> Löschen
                      </button>
                    )}
                  </div>
                </figcaption>
              </figure>
            );
          })}
        </div>
      )}
    </div>
  );
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}
